import { MouseEventHandler, PropsWithChildren } from 'react';
import Menu from '@engineering/icons/menu';
import Comment from '@engineering/icons/comment';
import User from '@engineering/icons/user';
import {
  BilligerMietwagenSiteViewContent,
  ContentLayout,
  HasInfoBanner,
  PageOfSiteProps,
} from '@silvertours/common-landingpages-view';
import {
  MembershipPopover,
  SupportPopover,
  useSiteInfo,
} from '@silvertours/front-entities';
import {
  PageLegacy,
  InfoBanner,
  Header,
  LoginModal,
  LoginForm,
  RegionalSettingsFlyout,
  useLogin,
  Footer,
} from '@silvertours/front-features';
import { useTranslations } from 'next-intl';
import { useStaticLinks } from './useStaticLinks';
import { RegionalSettingsWrapper, StyledHeader } from './Content.style';

type Page = HasInfoBanner;

const SupportMenu = () => <SupportPopover />;

const LoginMobileMenu = () => {
  const { isLoggedIn, showMobileLogin, setShowMobileLogin } = useLogin();

  const handleLoginClick: MouseEventHandler<Element> = event => {
    event.preventDefault();
    setShowMobileLogin(true);
  };
  const t = useTranslations('features.navigationLegacy.login');

  const handleSignUpClick = () => {
    window.location.assign(t('signup.href'));
  };

  if (isLoggedIn) {
    return <Header.LoggedInActions />;
  }

  return showMobileLogin ? (
    <LoginForm />
  ) : (
    <MembershipPopover
      handleLoginClick={handleLoginClick}
      handleSignUpClick={handleSignUpClick}
    />
  );
};

const ContentDisplay = ({
  children,
  page,
}: PageOfSiteProps<
  BilligerMietwagenSiteViewContent,
  ContentLayout<Page>,
  Record<string, unknown>
> &
  PropsWithChildren) => {
  const {
    getHeaderLinks,
    getFooterLinks,
    getLegalLinks,
    getSocialMediaLinks,
    getAppLinks,
  } = useStaticLinks();
  const headerLinks = getHeaderLinks(page.navigation.staticLinks);
  const footerLinks = getFooterLinks(page.navigation.staticLinks);
  const legalLinks = getLegalLinks(page.navigation.staticLinks);
  const socialMediaLinks = getSocialMediaLinks(page.navigation.staticLinks);
  const appLinks = getAppLinks(page.navigation.staticLinks);
  const t = useTranslations('features.navigationLegacy');

  const { setShowMobileLogin } = useLogin();

  const {
    featureToggles: { hideInfoline, regionalSettings },
  } = useSiteInfo();

  return (
    <PageLegacy.PageBase>
      <StyledHeader
        desktopActions={[
          {
            key: 'regional_settings',
            component: (
              <RegionalSettingsWrapper>
                <RegionalSettingsFlyout />
              </RegionalSettingsWrapper>
            ),
            hide: !regionalSettings,
          },
          {
            key: 'infoline',
            component: (
              <Header.SupportPopoverButton popupContent={<SupportMenu />} />
            ),
            hide: hideInfoline,
          },
          {
            key: 'login',
            component: <Header.LoginButton />,
          },
        ]}
        desktopLinks={headerLinks}
        mobileMenus={[
          {
            id: 'mobile-menu-contact',
            closeLabel: t('navigation.header.closeLabel'),
            iconLabel: t('navigation.header.contact'),
            content: SupportMenu,
            icon: Comment,
            hide: hideInfoline,
          },
          {
            id: 'mobile-menu-account',
            closeLabel: t('navigation.header.closeLabel'),
            iconLabel: t('login.menu.button.loggedIn'),
            content: LoginMobileMenu,
            icon: User,
            onClose: () => setShowMobileLogin(false),
          },
          {
            id: 'mobile-menu-hamburger',
            closeLabel: t('navigation.header.closeLabel'),
            iconLabel: t('navigation.header.menuLabel'),
            content: (closeMenu, open) => (
              <Header.MobileNavigationMenu
                links={headerLinks}
                navigationLabel={t('navigation.header.navigationLabel')}
                closeMenu={closeMenu}
                showRegionalSettings={regionalSettings}
                isOpen={open}
              />
            ),
            icon: Menu,
          },
        ]}
        translations={{
          navigationLabel: t('navigation.header.navigationLabel'),
          hiddenLinksLabel: t('navigation.header.hiddenLinksLabel'),
        }}
      >
        {page.content?.infoBanner && (
          <InfoBanner {...page.content.infoBanner} />
        )}
      </StyledHeader>
      <LoginModal />
      {children}
      <Footer
        footerLinks={footerLinks}
        legalLinks={legalLinks}
        socialMediaLinks={socialMediaLinks}
        appLinks={appLinks}
      />
    </PageLegacy.PageBase>
  );
};

export { ContentDisplay };
